import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Trophy, Undo2, AlertCircle, Check, ChevronRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { basketballScoringAPI } from './services/basketballScoringApi';
import BoxScore from './BoxScore';

/**
 * BasketballScoring
 *
 * Scoring for a basketball game, on the same footing as the other sports:
 * every action is appended to a log the server owns, and the score, the box
 * score and undo are all derived from it. The previous version kept its undo
 * stack in the browser, so a refresh — or a second person scoring the same
 * game — silently lost it.
 *
 * Handles the 'scoring' and 'completed' phases only; setup and team selection
 * belong to GameComponent, as with pickleball.
 */

// Actions that put points on the board.
const SHOT_TYPES = [
  { key: 'free_throw',    label: 'Free Throw', short: 'FT',  points: 1 },
  { key: 'field_goal',    label: '2 Points',   short: '2PT', points: 2 },
  { key: 'three_pointer', label: '3 Points',   short: '3PT', points: 3 },
];

// Recorded against a player without changing the score.
const STAT_TYPES = [
  { key: 'rebound',  label: 'Rebound'  },
  { key: 'assist',   label: 'Assist'   },
  { key: 'steal',    label: 'Steal'    },
  { key: 'block',    label: 'Block'    },
  { key: 'turnover', label: 'Turnover' },
  { key: 'foul',     label: 'Foul'     },
];

const EVENT_LABELS = {
  free_throw: 'Free throw', field_goal: '2pt', three_pointer: '3pt',
  rebound: 'Rebound', assist: 'Assist', steal: 'Steal',
  block: 'Block', turnover: 'Turnover', foul: 'Foul',
  substitution: 'Sub',
};

const BasketballScoring = ({
  gameCommonId,
  gameSettings: gameSettingsProp,
  participantAInfo,
  participantBInfo,
  onGameComplete,
  // Tells GameComponent whether anything has been recorded yet, which is what
  // gates its Match Settings / Team Selection buttons. Every other sport
  // reports this; basketball did not, so those buttons never became available
  // here at all.
  onGamePointsStatus,
  isScorer = true,
  // Pause/resume controls and the paused banner are owned by GameComponent.
  paused = false,
}) => {
  const { theme } = useTheme();

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Detailed scoring attributes each action to a player (and an assist) rather
  // than only moving the team total. Off by default: a casual scorer wants two
  // buttons, not a roster.
  const [detailedScoring, setDetailedScoring] = useState(false);
  const [pendingShot, setPendingShot] = useState(null);  // { team, type, isMade }
  const [assistPlayerId, setAssistPlayerId] = useState(null);

  const [statPanel, setStatPanel] = useState(null);      // { team, statKey }
  const [swapModal, setSwapModal] = useState(null);      // { side, outId, inId }
  const [swapping, setSwapping] = useState(false);

  const [showFinalScoreModal, setShowFinalScoreModal] = useState(false);
  const [finalScoreInputs, setFinalScoreInputs] = useState({ a: '', b: '' });
  const [finalScoreSubmitting, setFinalScoreSubmitting] = useState(false);

  const [showBoxScore, setShowBoxScore] = useState(false);
  const [lineupDraft, setLineupDraft] = useState(null);   // { side, picked: [id] }
  const completedNotifiedRef = useRef(false);

  const bb = game?.basketball;
  const gameComplete = game?.status === 'completed';
  const eventsCount = bb?.possessions?.length || 0;

  useEffect(() => {
    if (!onGamePointsStatus) return;
    onGamePointsStatus(eventsCount > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsCount]);
  const settings = game?.gameSettings || gameSettingsProp || {};
  const totalPeriods = settings.quarters || settings.periods || 4;
  const canScore = isScorer && !gameComplete && !paused && !saving;

  const teamAName = game?.participantAInfo?.name || participantAInfo?.name || 'Team A';
  const teamBName = game?.participantBInfo?.name || participantBInfo?.name || 'Team B';
  // The squad is the whole teamsheet; onCourt is who is playing right now. The
  // server owns that split, so both survive a refresh and a second scorer.
  const squadA = game?.participantAInfo?.players || participantAInfo?.players || [];
  const squadB = game?.participantBInfo?.players || participantBInfo?.players || [];
  const teamAPlayers = bb?.onCourtA || squadA;
  const teamBPlayers = bb?.onCourtB || squadB;
  const playersFor = (team) => (team === 1 ? teamAPlayers : teamBPlayers);
  const benchOf = (team) => (team === 1 ? bb?.benchA : bb?.benchB) || [];
  const squadOf = (team) => (team === 1 ? squadA : squadB);
  // Comes from the sport, not from here. Null means the sport sets no limit,
  // in which case there is no starting-lineup question to ask.
  const maxOnCourt = bb?.maxOnCourt ?? null;

  // Searches the full squads, not just who is on court: the player coming off
  // in a substitution has already left the on-court list by the time that row
  // is rendered, and would otherwise show as "player".
  // Searches the whole squad, not just who is on court: a player who has been
  // substituted off still appears in the log and must still be nameable.
  const nameOf = useCallback((playerId) => {
    if (!playerId) return null;
    const hit = [...squadA, ...squadB].find(p => String(p?.id) === String(playerId));
    return hit?.name || null;
  }, [squadA, squadB]);

  // ─── Load ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!gameCommonId) { setLoading(false); return; }
    try {
      setError(null);
      setGame(await basketballScoringAPI.getById(gameCommonId));
    } catch (err) {
      setError(err.message || 'Could not load the game');
    } finally {
      setLoading(false);
    }
  }, [gameCommonId]);

  useEffect(() => { load(); }, [load]);

  // Announced once, not on every re-render of an already-complete game.
  useEffect(() => {
    if (gameComplete && !completedNotifiedRef.current) {
      completedNotifiedRef.current = true;
      onGameComplete?.();
    }
  }, [gameComplete, onGameComplete]);

  /** Every mutation returns the whole game, so state is replaced, never patched. */
  const apply = useCallback((updated) => { setGame(updated); return updated; }, []);

  // ─── Scoring ───────────────────────────────────────────────────────────────

  const record = useCallback(async (action) => {
    if (!canScore) return;
    setSaving(true);
    try {
      apply(await basketballScoringAPI.recordEvent(gameCommonId, action));
    } catch (err) {
      toast.error(err.message || 'Could not record that');
    } finally {
      setSaving(false);
    }
  }, [canScore, gameCommonId, apply]);

  /**
   * A shot press. Simple mode scores straight away for the team; detailed mode
   * opens the player picker, since attributing it is the point of the mode.
   */
  const handleShot = useCallback((team, type, isMade = true) => {
    if (!canScore) return;
    if (!detailedScoring) {
      record({ team, eventType: type.key, isMade });
      return;
    }
    setPendingShot({ team, type, isMade });
    setAssistPlayerId(null);
  }, [canScore, detailedScoring, record]);

  const confirmShot = useCallback(async (playerId) => {
    if (!pendingShot) return;
    const { team, type, isMade } = pendingShot;
    setPendingShot(null);
    await record({
      team,
      eventType: type.key,
      isMade,
      playerId: playerId || null,
      // An assist only makes sense on a made shot, and the server drops it
      // otherwise — mirrored here so the UI never implies it was recorded.
      assistPlayerId: isMade ? assistPlayerId : null,
    });
    setAssistPlayerId(null);
  }, [pendingShot, assistPlayerId, record]);

  const handleStat = useCallback(async (team, statKey, playerId) => {
    setStatPanel(null);
    await record({ team, eventType: statKey, playerId: playerId || null });
  }, [record]);

  const handleUndo = useCallback(async () => {
    if (!isScorer || gameComplete || saving) return;
    setSaving(true);
    try {
      apply(await basketballScoringAPI.undoLast(gameCommonId));
      toast.success('Last action undone');
    } catch (err) {
      toast.error(err.message || 'Nothing to undo');
    } finally {
      setSaving(false);
    }
  }, [isScorer, gameComplete, saving, gameCommonId, apply]);

  const handlePeriod = useCallback(async (period) => {
    if (!canScore || period < 1) return;
    setSaving(true);
    try {
      apply(await basketballScoringAPI.setPeriod(gameCommonId, period));
    } catch (err) {
      toast.error(err.message || 'Could not change the period');
    } finally {
      setSaving(false);
    }
  }, [canScore, gameCommonId, apply]);

  const handleComplete = useCallback(async () => {
    if (!isScorer || gameComplete || saving) return;
    setSaving(true);
    try {
      apply(await basketballScoringAPI.completeGame(gameCommonId));
      toast.success('Game complete');
    } catch (err) {
      toast.error(err.message || 'Could not complete the game');
    } finally {
      setSaving(false);
    }
  }, [isScorer, gameComplete, saving, gameCommonId, apply]);

  const handleSubmitFinalScore = useCallback(async () => {
    const a = parseInt(finalScoreInputs.a, 10);
    const b = parseInt(finalScoreInputs.b, 10);
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) {
      toast.error('Enter both scores');
      return;
    }
    setFinalScoreSubmitting(true);
    try {
      apply(await basketballScoringAPI.submitFinalScore(gameCommonId, {
        participantAScore: a, participantBScore: b,
      }));
      setShowFinalScoreModal(false);
      toast.success('Final score recorded');
    } catch (err) {
      toast.error(err.message || 'Could not record the final score');
    } finally {
      setFinalScoreSubmitting(false);
    }
  }, [finalScoreInputs, gameCommonId, apply]);

  const handleSaveLineup = useCallback(async () => {
    if (!lineupDraft) return;
    setSaving(true);
    try {
      apply(await basketballScoringAPI.setLineup(gameCommonId, {
        side: lineupDraft.side, playerIds: lineupDraft.picked,
      }));
      setLineupDraft(null);
    } catch (err) {
      toast.error(err.message || 'Could not set the lineup');
    } finally {
      setSaving(false);
    }
  }, [lineupDraft, gameCommonId, apply]);

  const handleConfirmSwap = useCallback(async () => {
    if (!swapModal?.outId || !swapModal?.inId) return;
    setSwapping(true);
    try {
      apply(await basketballScoringAPI.swapPlayer(gameCommonId, {
        side: swapModal.side,
        oldPlayerId: swapModal.outId,
        newPlayer: { id: swapModal.inId },
      }));
      setSwapModal(null);
      toast.success('Substitution made');
    } catch (err) {
      toast.error(err.message || 'Could not substitute');
    } finally {
      setSwapping(false);
    }
  }, [swapModal, gameCommonId, apply]);

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return <div className={`${theme.textMuted} text-center py-12`}>Loading game…</div>;
  }

  if (error) {
    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
        <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-500" />
        <p className={`${theme.text} mb-3`}>{error}</p>
        <button onClick={load} className={`px-4 py-2 rounded-lg border ${theme.border} ${theme.text}`}>
          Try again
        </button>
      </div>
    );
  }

  const scoreA = bb?.scoreA ?? game?.participantAScore ?? 0;
  const scoreB = bb?.scoreB ?? game?.participantBScore ?? 0;

  const renderTeamCard = (team) => {
    const isA = team === 1;
    const players = playersFor(team);
    const bench = benchOf(team);
    const canSwap = settings.swappable !== false && isScorer && !gameComplete && bench.length > 0;

    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
        <div className={`text-sm ${theme.textMuted} mb-2 truncate`}>{isA ? teamAName : teamBName}</div>
        <div className={`text-6xl font-bold ${theme.text} mb-2`}>{isA ? scoreA : scoreB}</div>
        {canSwap && (
          <button
            type="button"
            onClick={() => setSwapModal({ side: isA ? 'A' : 'B', outId: null, inId: null })}
            className={`mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${theme.border} ${theme.textMuted} hover:opacity-80`}
          >
            <RefreshCw className="w-3 h-3" /> Substitute
          </button>
        )}
      </div>
    );
  };

  const renderScoringButtons = (team) => {
    const tone = team === 1
      ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
      : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800';
    return (
      // flex + gap rather than space-y-2: the space-y utility resolves to
      // margin-top: 0 in this build, which left the +1/+2/+3 buttons welded
      // into one solid block with no seam between them.
      <div className="flex flex-col gap-2">
        <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} text-center truncate`}>
          {team === 1 ? teamAName : teamBName}
        </div>
        {SHOT_TYPES.map(type => (
          <button
            key={type.key}
            onClick={() => handleShot(team, type, true)}
            disabled={!canScore}
            className={`w-full py-4 rounded-lg font-semibold text-white transition-transform active:scale-95 disabled:opacity-50 ${tone}`}
          >
            +{type.points} <span className="opacity-80 text-sm">{type.short}</span>
          </button>
        ))}
        {/* Misses and stats are detailed-mode only: both exist to build a box
            score, which needs a named player anyway. */}
        {detailedScoring && (
          <>
            <div className="grid grid-cols-3 gap-1">
              {SHOT_TYPES.map(type => (
                <button
                  key={type.key}
                  onClick={() => handleShot(team, type, false)}
                  disabled={!canScore}
                  className={`py-2 rounded-lg text-xs border ${theme.border} ${theme.textMuted} disabled:opacity-50`}
                >
                  {type.short} miss
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-1">
              {STAT_TYPES.map(stat => (
                <button
                  key={stat.key}
                  onClick={() => setStatPanel({ team, statKey: stat.key })}
                  disabled={!canScore}
                  className={`py-2 rounded-lg text-xs border ${theme.border} ${theme.textMuted} disabled:opacity-50`}
                >
                  {stat.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderPlayerPicker = ({ title, subtitle, players, onPick, onCancel, before, extra }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className={`${theme.card} rounded-lg border ${theme.border} p-5 w-full max-w-md`} onClick={e => e.stopPropagation()}>
        <h3 className={`${theme.text} text-lg font-semibold mb-1`}>{title}</h3>
        {subtitle && <p className={`text-sm ${theme.textMuted} mb-3`}>{subtitle}</p>}
        {before}
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {players.length === 0 && (
            <p className={`text-sm ${theme.textMuted} text-center py-4`}>No players available.</p>
          )}
          {players.map(p => (
            <button
              key={p.id}
              onClick={() => onPick(p)}
              className={`w-full text-left px-4 py-3 rounded-lg border ${theme.border} ${theme.text} hover:opacity-80`}
            >
              {p.name}
            </button>
          ))}
        </div>
        {extra}
        <button onClick={onCancel} className={`w-full mt-3 py-2 rounded-lg ${theme.textMuted} border ${theme.border}`}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {renderTeamCard(1)}
        {renderTeamCard(2)}
      </div>

      <div className={`${theme.card} rounded-lg border ${theme.border} p-3 flex items-center justify-between`}>
        <span className={`text-sm ${theme.textMuted}`}>Period</span>
        <div className="flex items-center gap-1">
          {Array.from({ length: totalPeriods }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => handlePeriod(n)}
              disabled={!canScore}
              className={`w-9 h-9 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-50 ${
                (bb?.period ?? 1) === n ? 'bg-blue-600 text-white border-blue-600' : `${theme.border} ${theme.text}`
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {gameComplete ? (
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
          <Trophy className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
          <p className={`${theme.text} text-lg font-semibold`}>
            {scoreA === scoreB ? 'Tied' : `${game.winner === 'A' ? teamAName : teamBName} won`}
          </p>
          <p className={`${theme.textMuted} text-sm mt-1`}>{scoreA} – {scoreB}</p>
        </div>
      ) : (
        <>
          {isScorer && (
            <label className={`flex flex-wrap items-center gap-2 text-sm ${theme.text} px-1`}>
              <input
                type="checkbox"
                checked={detailedScoring}
                onChange={e => { setDetailedScoring(e.target.checked); setPendingShot(null); setStatPanel(null); }}
                className="w-4 h-4"
              />
              <span>Enable Detailed Scoring</span>
              <span className={`text-xs ${theme.textMuted}`}>— per player, with misses and stats</span>
            </label>
          )}

          {/* A squad may carry more players than it may field, so who starts is
              a decision the scorer has to make — not something to guess by
              taking the first five off the teamsheet. */}
          {isScorer && bb?.needsLineup && maxOnCourt && (
            <div className={`${theme.card} rounded-lg border border-amber-500/60 p-4`}>
              <p className={`${theme.text} font-semibold mb-1`}>Choose who starts</p>
              <p className={`text-sm ${theme.textMuted} mb-3`}>
                Basketball plays {maxOnCourt} a side. Pick the starting {maxOnCourt} for each
                oversized squad — everyone else stays available to substitute on.
              </p>
              <div className="flex flex-wrap gap-2">
                {[1, 2].map(team => (
                  squadOf(team).length > maxOnCourt && (
                    <button
                      key={team}
                      onClick={() => setLineupDraft({
                        side: team === 1 ? 'A' : 'B',
                        picked: playersFor(team).slice(0, maxOnCourt).map(p => String(p.id)),
                      })}
                      className={`px-3 py-2 rounded-lg text-sm border ${theme.border} ${theme.text}`}
                    >
                      Set {team === 1 ? teamAName : teamBName} ({squadOf(team).length} players)
                    </button>
                  )
                ))}
              </div>
            </div>
          )}

          {isScorer && (
            <div className="grid grid-cols-2 gap-3">
              {renderScoringButtons(1)}
              {renderScoringButtons(2)}
            </div>
          )}

          {/* Skipping play-by-play is offered only before anything is recorded,
              so it can never silently discard a log someone has built up. */}
          {isScorer && eventsCount === 0 && (
            <button
              onClick={() => setShowFinalScoreModal(true)}
              className={`w-full text-sm py-2 rounded-lg border ${theme.border} ${theme.textMuted}`}
            >
              Enter Final Score Instead
            </button>
          )}

          {isScorer && (
            <div className="flex gap-2">
              <button
                onClick={handleUndo}
                disabled={saving || eventsCount === 0}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-50`}
              >
                <Undo2 className="w-4 h-4" /> Undo
              </button>
              <button
                onClick={handleComplete}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50"
              >
                <Check className="w-4 h-4" /> Complete Game
              </button>
            </div>
          )}
        </>
      )}

      {/* Recent actions — so Undo names what it is about to remove */}
      {eventsCount > 0 && (
        <div className={`${theme.card} rounded-lg border ${theme.border} p-3`}>
          <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-2`}>Recent</div>
          <div className="space-y-1">
            {bb.possessions.slice(0, 5).map(p => (
              <div key={p.id} className={`text-sm flex items-center gap-2 ${theme.textSecondary}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.team === 1 ? 'bg-green-500' : 'bg-blue-500'}`} />
                <span className={theme.text}>{EVENT_LABELS[p.eventType] || p.eventType}</span>
                {!p.isMade && p.eventType !== 'substitution' && (
                  <span className="text-xs text-red-400">missed</span>
                )}
                {p.playerId && <span className={`${theme.textMuted} truncate`}>{nameOf(p.playerId)}</span>}
                {p.eventType === 'substitution' && p.relatedPlayerId && (
                  <span className={`text-xs ${theme.textMuted} truncate`}>for {nameOf(p.relatedPlayerId) || 'player'}</span>
                )}
                {p.eventType !== 'substitution' && p.assistPlayerId && (
                  <span className={`text-xs ${theme.textMuted} truncate`}>(ast {nameOf(p.assistPlayerId)})</span>
                )}
                <span className={`ml-auto text-xs ${theme.textMuted} shrink-0`}>Q{p.period}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {bb && Object.keys(bb.boxScore || {}).length > 0 && (
        <div className={`${theme.card} rounded-lg border ${theme.border} p-3`}>
          <button
            onClick={() => setShowBoxScore(v => !v)}
            className={`w-full flex items-center justify-between text-sm ${theme.text}`}
          >
            <span className="font-semibold">Box Score</span>
            <ChevronRight className={`w-4 h-4 transition-transform ${showBoxScore ? 'rotate-90' : ''}`} />
          </button>
          {showBoxScore && (
            <div className="mt-3">
              <BoxScore
                boxScore={bb.boxScore}
                teams={[
                  { name: teamAName, players: squadA },
                  { name: teamBName, players: squadB },
                ]}
                periodScores={bb.periodScores}
                compact
              />
            </div>
          )}
        </div>
      )}

      {pendingShot && renderPlayerPicker({
        title: `${pendingShot.isMade ? '' : 'Missed '}${pendingShot.type.label} — ${pendingShot.team === 1 ? teamAName : teamBName}`,
        subtitle: pendingShot.isMade ? 'Pick the assist first if there was one, then tap the scorer to record it.' : 'Who took the shot?',
        // Nobody assists their own basket, so whoever is credited with the
        // assist drops out of the scorer list rather than sitting there as a
        // choice that would record a contradiction.
        players: playersFor(pendingShot.team).filter(p => p.id !== assistPlayerId),
        onPick: p => confirmShot(p.id),
        onCancel: () => { setPendingShot(null); setAssistPlayerId(null); },
        before: pendingShot.isMade ? (
          // Set apart in its own panel with real buttons. As bare text-coloured
          // chips these read as labels, and a scorer never realised the assist
          // was theirs to pick — the step it exists for was being skipped.
          <div className={`mb-4 rounded-lg border ${theme.border} p-3`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-semibold ${theme.text}`}>Assisted by</span>
              <span className={`text-xs ${theme.textMuted}`}>optional — tap a name</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {playersFor(pendingShot.team).map(p => {
                const picked = assistPlayerId === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setAssistPlayerId(prev => (prev === p.id ? null : p.id))}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                      picked
                        ? 'bg-blue-600 text-white border-blue-600'
                        : `${theme.text} border-blue-500/50 hover:border-blue-500`
                    }`}
                  >
                    {picked && '✓ '}{p.name?.split(' ')[0] || p.name}
                  </button>
                );
              })}
            </div>
            {assistPlayerId && (
              <button
                onClick={() => setAssistPlayerId(null)}
                className={`mt-2 text-xs ${theme.textMuted} underline`}
              >
                Clear assist
              </button>
            )}
          </div>
        ) : null,
        extra: (
          <button
            onClick={() => confirmShot(null)}
            className={`w-full mt-3 py-2 rounded-lg text-sm border ${theme.border} ${theme.textMuted}`}
          >
            Record without a player
          </button>
        ),
      })}

      {statPanel && renderPlayerPicker({
        title: STAT_TYPES.find(s => s.key === statPanel.statKey)?.label,
        subtitle: statPanel.team === 1 ? teamAName : teamBName,
        players: playersFor(statPanel.team),
        onPick: p => handleStat(statPanel.team, statPanel.statKey, p.id),
        onCancel: () => setStatPanel(null),
      })}

      {/* One substitution, made in one place: choose who comes off, who comes
          on, then confirm. Previously each player on court carried their own
          button, which put five near-identical controls on every team card. */}
      {swapModal && (() => {
        const team = swapModal.side === 'A' ? 1 : 2;
        const onCourt = playersFor(team);
        const bench = benchOf(team);
        const ready = swapModal.outId && swapModal.inId;
        const pick = (key) => (pid) => setSwapModal(prev => ({
          ...prev, [key]: prev[key] === pid ? null : pid,
        }));
        const chip = (p, selected, onClick) => (
          <button
            key={p.id}
            onClick={() => onClick(String(p.id))}
            disabled={swapping}
            className={`w-full text-left px-4 py-2.5 rounded-lg border transition-colors disabled:opacity-50 ${
              selected ? 'bg-blue-600 text-white border-blue-600' : `${theme.border} ${theme.text}`
            }`}
          >
            {p.name}
          </button>
        );

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !swapping && setSwapModal(null)}>
            <div className={`${theme.card} rounded-lg border ${theme.border} p-5 w-full max-w-md`} onClick={e => e.stopPropagation()}>
              <h3 className={`${theme.text} text-lg font-semibold mb-1`}>
                Substitution — {team === 1 ? teamAName : teamBName}
              </h3>
              <p className={`text-sm ${theme.textMuted} mb-4`}>
                Anything already recorded stays with the player coming off.
              </p>

              <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1`}>Coming off</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto mb-4">
                {onCourt.map(p => chip(p, swapModal.outId === String(p.id), pick('outId')))}
              </div>

              <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1`}>Coming on</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {bench.length === 0 && (
                  <p className={`text-sm ${theme.textMuted} py-2`}>Everyone in this squad is already playing.</p>
                )}
                {bench.map(p => chip(p, swapModal.inId === String(p.id), pick('inId')))}
              </div>

              <button
                onClick={handleConfirmSwap}
                disabled={!ready || swapping}
                className="w-full mt-4 py-3 rounded-lg font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {swapping ? 'Substituting…' : 'Confirm substitution'}
              </button>
              <button
                onClick={() => setSwapModal(null)}
                disabled={swapping}
                className={`w-full mt-2 py-2 rounded-lg ${theme.textMuted} border ${theme.border} disabled:opacity-50`}
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      {lineupDraft && (() => {
        const team = lineupDraft.side === 'A' ? 1 : 2;
        const squad = squadOf(team);
        const toggle = (pid) => setLineupDraft(prev => {
          const has = prev.picked.includes(pid);
          if (has) return { ...prev, picked: prev.picked.filter(x => x !== pid) };
          if (prev.picked.length >= maxOnCourt) return prev;   // never more than the sport allows
          return { ...prev, picked: [...prev.picked, pid] };
        });
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !saving && setLineupDraft(null)}>
            <div className={`${theme.card} rounded-lg border ${theme.border} p-5 w-full max-w-md`} onClick={e => e.stopPropagation()}>
              <h3 className={`${theme.text} text-lg font-semibold mb-1`}>
                Starting {maxOnCourt} — {team === 1 ? teamAName : teamBName}
              </h3>
              <p className={`text-sm ${theme.textMuted} mb-3`}>
                {lineupDraft.picked.length} of {maxOnCourt} chosen. The rest of the squad can be
                substituted on later.
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {squad.map(p => {
                  const pid = String(p.id);
                  const on = lineupDraft.picked.includes(pid);
                  return (
                    <button
                      key={pid}
                      onClick={() => toggle(pid)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                        on ? 'bg-blue-600 text-white border-blue-600' : `${theme.border} ${theme.text}`
                      }`}
                    >
                      <span>{p.name}</span>
                      <span className="text-xs opacity-80">{on ? 'Playing' : 'Bench'}</span>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={handleSaveLineup}
                disabled={saving || lineupDraft.picked.length !== maxOnCourt}
                className="w-full mt-3 py-3 rounded-lg font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : `Confirm ${lineupDraft.picked.length}/${maxOnCourt}`}
              </button>
              <button onClick={() => setLineupDraft(null)} disabled={saving}
                className={`w-full mt-2 py-2 rounded-lg ${theme.textMuted} border ${theme.border}`}>
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      {showFinalScoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !finalScoreSubmitting && setShowFinalScoreModal(false)}>
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 w-full max-w-md`} onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className={`${theme.text} text-lg font-semibold`}>Enter Final Score</h3>
              <button onClick={() => setShowFinalScoreModal(false)} aria-label="Close" className={theme.textMuted}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className={`text-sm ${theme.textMuted} mb-4`}>Skips play-by-play and completes the game.</p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-green-500 truncate">{teamAName}</label>
                <input
                  type="number" min="0" inputMode="numeric"
                  value={finalScoreInputs.a}
                  onChange={e => setFinalScoreInputs(prev => ({ ...prev, a: e.target.value }))}
                  disabled={finalScoreSubmitting}
                  className={`w-full px-3 py-2 border rounded-lg text-center text-lg font-semibold ${theme.input}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide text-blue-400 truncate">{teamBName}</label>
                <input
                  type="number" min="0" inputMode="numeric"
                  value={finalScoreInputs.b}
                  onChange={e => setFinalScoreInputs(prev => ({ ...prev, b: e.target.value }))}
                  disabled={finalScoreSubmitting}
                  className={`w-full px-3 py-2 border rounded-lg text-center text-lg font-semibold ${theme.input}`}
                />
              </div>
            </div>
            <button
              onClick={handleSubmitFinalScore}
              disabled={finalScoreSubmitting}
              className="w-full py-3 rounded-lg font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {finalScoreSubmitting ? 'Completing…' : 'Complete Game'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasketballScoring;
